import { useMemo } from 'react'
import type { PeriodGroup } from '@/services/api/timetable'

interface PeriodGroupFilterBarProps {
  groups: PeriodGroup[]
  activeFilter: 'all' | 'current' | 'future' | 'expired' | 'duplicates'
  selectedTeachers: Set<string>
  onFilterChange: (filter: 'all' | 'current' | 'future' | 'expired' | 'duplicates') => void
  showOnlySelected: boolean
  onToggleShowOnlySelected: () => void
}

export function PeriodGroupFilterBar({
  groups,
  activeFilter,
  selectedTeachers,
  onFilterChange,
  showOnlySelected,
  onToggleShowOnlySelected,
}: PeriodGroupFilterBarProps) {
  // Calculate counts for each filter
  const counts = useMemo(() => {
    const today = new Date()
    
    return {
      all: groups.length,
      current: groups.filter(g => g.is_current).length,
      future: groups.filter(g => {
        if (!g.valid_from) return false
        return new Date(g.valid_from) > today
      }).length,
      expired: groups.filter(g => {
        if (!g.valid_to) return false
        return new Date(g.valid_to) < today
      }).length,
      duplicates: groups.filter(g => {
        // Teachers appearing in multiple groups
        const teacherIds = new Set<string>()
        groups.forEach(gr => {
          gr.teachers.forEach(t => teacherIds.add(t.teacher_id))
        })
        // Count how many groups each teacher appears in
        const teacherGroupCount = new Map<string, number>()
        groups.forEach(gr => {
          gr.teachers.forEach(t => {
            teacherGroupCount.set(t.teacher_id, (teacherGroupCount.get(t.teacher_id) || 0) + 1)
          })
        })
        // Return groups that have teachers appearing in multiple groups
        return g.teachers.some(t => (teacherGroupCount.get(t.teacher_id) ?? 0) > 1)
      }).length,
    }
  }, [groups])

  const filters: Array<{ key: 'all' | 'current' | 'future' | 'expired' | 'duplicates'; label: string }> = [
    { key: 'all', label: 'Alle' },
    { key: 'current', label: 'Aktuell' },
    { key: 'future', label: 'Zukünftig' },
    { key: 'expired', label: 'Abgelaufen' },
    { key: 'duplicates', label: 'Duplikate' },
  ]

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="flex items-center gap-1 bg-gray-50 rounded-lg p-1">
        {filters.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => onFilterChange(key)}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              activeFilter === key
                ? 'bg-blue-100 text-blue-700'
                : 'text-gray-600 hover:bg-gray-200'
            }`}
          >
            {label}
            <span className={`ml-1.5 text-xs ${
              activeFilter === key ? 'text-blue-600' : 'text-gray-400'
            }`}>
              ({counts[key]})
            </span>
          </button>
        ))}
      </div>

      <div className="h-6 w-px bg-gray-200 mx-2" />

      <button
        onClick={onToggleShowOnlySelected}
        className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
          showOnlySelected
            ? 'bg-blue-100 text-blue-700'
            : 'text-gray-600 hover:bg-gray-200'
        }`}
      >
        <span>Nur Ausgewählte</span>
        {selectedTeachers.size > 0 && (
          <span className={`text-xs ${showOnlySelected ? 'text-blue-600' : 'text-gray-400'}`}>
            ({selectedTeachers.size})
          </span>
        )}
      </button>
    </div>
  )
}
